import { Alert, AlertTitle, Box, Typography } from '@mui/material';

import type { ApprovalReadiness } from '../../api/kyc.api.js';
import type { ApprovalWarning } from '../../api/onboarding.api.js';

import {
  screeningMatchHeadline,
  screeningWarningHeadline,
  screeningWarningLine,
  splitScreeningWarnings,
} from './kyc-approval.js';

/**
 * Why Approve is unavailable — the same checklist the approve route enforces,
 * shown before the officer commits rather than as a refusal afterwards. Shared
 * by the customer's KYC tab and the application review.
 */
export function ApprovalBlockers({ readiness }: {
  readiness: ApprovalReadiness | null | undefined;
}): JSX.Element | null {
  if (readiness === null || readiness === undefined || readiness.ready) return null;
  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      Approval is blocked until these are resolved:
      <Box component="ul" sx={{ pl: 2, m: 0, mt: 0.5 }}>
        {readiness.blockers.map((blocker) => (
          <li key={blocker.code}>
            <Typography variant="body2">{blocker.detail}</Typography>
          </li>
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary">
        Documents are reviewed on the Onboarding application; identity checks on the
        customer’s Provider tab. Rejecting the case stays available.
      </Typography>
    </Alert>
  );
}

/**
 * Profiles with no usable sanctions screening result (DEV-114).
 *
 * A business was approved in production with neither the company nor its
 * director ever screened, and nothing on the approve screen said so. This is a
 * WARNING, not a blocker (user decision): Approve stays available, and an
 * approval made over it is recorded as made without a screening result. Every
 * approve dialog shows it above the reason field, one line per profile.
 */
export function ApprovalWarnings({ readiness }: {
  readiness: Pick<ApprovalReadiness, 'warnings'> | null | undefined;
}): JSX.Element | null {
  // A possible match is a finding, not a gap (compliance review, DEV-114): it
  // gets its own error-level alert and is never filed under "no usable result".
  const { matches, missing } = splitScreeningWarnings(readiness);
  if (matches.length === 0 && missing.length === 0) return null;
  return (
    <Box sx={{ mb: 2 }} data-testid="screening-warnings">
      {matches.length > 0 ? (
        <Alert severity="error" sx={{ mb: missing.length > 0 ? 1 : 0 }} data-testid="screening-matches">
          <AlertTitle>Possible sanctions match not reviewed</AlertTitle>
          <Typography variant="body2">{screeningMatchHeadline(matches.length)}</Typography>
          <WarningLines warnings={matches} />
        </Alert>
      ) : null}
      {missing.length > 0 ? (
        <Alert severity="warning" data-testid="screening-missing">
          <AlertTitle>No usable sanctions screening result</AlertTitle>
          <Typography variant="body2">{screeningWarningHeadline(missing.length)}</Typography>
          <WarningLines warnings={missing} />
        </Alert>
      ) : null}
    </Box>
  );
}

function WarningLines({ warnings }: { warnings: readonly ApprovalWarning[] }): JSX.Element {
  return (
    <Box component="ul" sx={{ pl: 2, m: 0, mt: 0.5 }}>
      {warnings.map((warning, index) => (
        // Two unnamed directors share a label; the position keeps keys unique.
        <li key={`${String(index)}:${warning.profile}`}>
          <Typography variant="body2">{screeningWarningLine(warning)}</Typography>
        </li>
      ))}
    </Box>
  );
}
